import { Grid } from '@material-ui/core';
import { Content, Page } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import {
  githubMyPrsApiRef,
  MyOpenPRsCard,
  PRsForReviewCard,
} from '@internal/backstage-plugin-github-my-prs';
import {
  jiraMyTasksApiRef,
  MyOpenTasksCard,
} from '@internal/backstage-plugin-jira-my-tasks';
import { AlertBanners } from './AlertBanners';
import { GreetingHeader } from './GreetingHeader';
import { TeamOnCallCard } from './TeamOnCallCard';

/**
 * My Day — a single destination for daily workflow context: a personalised
 * greeting followed by the user's open PRs, PRs waiting for their review,
 * and open Jira tasks as compact list widgets (PAT-605).
 *
 * The page is enabled for everyone: the route (App.tsx) and the sidebar item
 * (modules/nav/Sidebar.tsx) render unconditionally. It was previously gated
 * behind an opt-in `my-day` feature flag, which was removed once the feature
 * was validated.
 */
export const MyDayPage = () => {
  const githubMyPrsApi = useApi(githubMyPrsApiRef);
  const jiraMyTasksApi = useApi(jiraMyTasksApiRef);

  const { value: openPrs, error: openPrsError } = useAsync(
    () => githubMyPrsApi.getOpenPullRequests(),
    [githubMyPrsApi],
  );
  // Same "teams" default as PRsForReviewCard's own initial view, so the
  // header count and the card's default-view count agree.
  const { value: reviewResult, error: reviewError } = useAsync(
    () => githubMyPrsApi.getReviewRequestedPullRequests('teams'),
    [githubMyPrsApi],
  );
  const { value: tasksResult, error: tasksError } = useAsync(
    () => jiraMyTasksApi.getOpenTasks(),
    [jiraMyTasksApi],
  );

  // Each of the three widgets below already renders its own error panel on
  // failure; this only prevents the header's aggregated stats from silently
  // reporting "All caught up" when one of its own fetches failed.
  const statsError = Boolean(openPrsError || reviewError || tasksError);

  const urgentCount = (openPrs ?? []).filter(
    pr => pr.reviewStatus === 'CHANGES_REQUESTED',
  ).length;

  return (
    <Page themeId="home">
      <Content>
        <GreetingHeader
          stats={{
            urgentCount,
            reviewCount: reviewResult?.prs.length ?? 0,
            prCount: (openPrs ?? []).length,
            taskCount: tasksResult?.tasks.length ?? 0,
          }}
          statsError={statsError}
        />
        <AlertBanners />
        <Grid container spacing={3} data-testid="my-day-widgets">
          {/* PAT-425: open PRs authored by the current user */}
          <Grid item xs={12} md={6}>
            <MyOpenPRsCard />
          </Grid>
          {/* PAT-426: PRs waiting for the current user's review */}
          <Grid item xs={12} md={6}>
            <PRsForReviewCard />
          </Grid>
          {/* PAT-604: current user's open Jira issues */}
          <Grid item xs={12} md={6}>
            <MyOpenTasksCard />
          </Grid>
          {/* PAT-605: on-call placeholder — no rotation data source yet */}
          <Grid item xs={12} md={6}>
            <TeamOnCallCard />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
